'use client';
import React, { useState } from 'react';
import { sendGAEvent, sendGTMEvent } from '@next/third-parties/google';
import Image from 'next/image';
import { useReCaptcha } from 'next-recaptcha-v3';
import styled from 'styled-components';
import { z } from 'zod';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import image from '@/public/Image-main_2.webp';
import { devices } from '@/styles/breakpoints';

const schema = z.object({
  email: z.string().email(),
});

export const Subscribe = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const { executeRecaptcha } = useReCaptcha();

  const onSend = async () => {
    if (!email) return;
    try {
      await schema.parse({ email });

      const token = await executeRecaptcha('subscribe');

      const res = await fetch('/api/form/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          referer: 'https://orcatec.com',
        },
        body: JSON.stringify({
          subject: 'Subscribe',
          data: email,
          token,
        }),
      });
      const result = await res.json();
      if (!result?.success) {
        throw new Error('Form submission failed');
      }

      sendGTMEvent({
        event: 'form_sent',
        value: process.env.NEXT_PUBLIC_GOOGLE_TAG,
      });
      sendGAEvent([{ event: 'form_sent' }]);
      setEmail('');
    } catch (error) {
      if (error instanceof z.ZodError) {
        console.error('Validation error:', error.errors);
        setError(true);
      }
    }
  };
  return (
    <Wrapper>
      <FormWrapper>
        <Title>Get our latest news</Title>
        <InputWrapper>
          <EmailInput
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);

              setError(false);
            }}
            placeholder="Enter your email"
          />
          {error && <ErrorMessage>Email is invalid</ErrorMessage>}
          <ButtonWrapper>
            <Button onClick={onSend} fullWidth={true} className="secondary">
              <span>Subscribe</span>
            </Button>
          </ButtonWrapper>
        </InputWrapper>
      </FormWrapper>

      <ImageWrapper>
        <Image src={image} alt="image" />
      </ImageWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 24px 0px 0px 0px;
  align-self: stretch;
  background: transparent;
  @media only screen and ${devices.desktop} {
    display: flex;
    align-items: start;
    justify-content: space-between;
    padding-top: 56px;
  }
`;

const Title = styled.h3`
  color: #192d52;
  font-family: var(--font-roboto);
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  margin-bottom: 24px;
  @media only screen and ${devices.desktop} {
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 32px;
  }
`;

const EmailInput = styled(Input)`
  padding: 6px 16px;
  height: 48px;
  margin-bottom: 16px;
  font-size: 14px;
  @media only screen and ${devices.desktop} {
    width: 363px;
    margin-bottom: 0px;
  }
`;

const ButtonWrapper = styled.div`
  margin-bottom: 24px;
  @media only screen and ${devices.desktop} {
    width: 120px;
    margin-bottom: 0px;
  }
`;

const ImageWrapper = styled.div`
  line-height: 0;
  display: flex;
  justify-content: center;
  & img {
    display: block;
    width: 100%;
    height: auto;
  }
  @media only screen and ${devices.desktop} {
    img {
      margin-left: 100px;
    }
  }
`;

const FormWrapper = styled.div`
  @media only screen and ${devices.desktop} {
    margin-top: 24px;
  }
`;
const InputWrapper = styled.div`
  position: relative;
  @media only screen and ${devices.desktop} {
    display: flex;
    align-items: center;
    gap: 26px;
    width: 100%;
  }
`;

const ErrorMessage = styled.p`
  position: absolute;
  bottom: -20px;
  color: var(--errorColor);
  font-family: var(--font-roboto);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
`;
